import { Button, Loading, Modal, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { addToast } from "@/utils";
import { Company } from "@/redux/slices/company/types";
import { useUpdateCompanyMutation } from "@/redux/apis/company/companyApi";
import { UpdateCompanyParams } from "@/redux/apis/company/types";

interface RemoveCompanyModalProps {
  open: boolean;
  onClose: () => void;
  selectedCompany?: Company;
}

export const RemoveCompanyModal = ({ open, onClose, selectedCompany }: RemoveCompanyModalProps) => {
  const [updateCompany, { isLoading }] = useUpdateCompanyMutation();

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  const handleConfirmDelete = () => {
    // TODO: Figure out why, in the backend, the entire company object gets updated when I just update a couple fields.
    if (selectedCompany) {
      const { invoice, pricing, notes } = selectedCompany;
      const companyData: UpdateCompanyParams = {
        uuid: selectedCompany.id,
        name: selectedCompany.name,
        address: selectedCompany.address,
        phone: selectedCompany.phone,
        business_registration_type: selectedCompany.businessRegistrationType,
        business_registration_id: selectedCompany.businessRegistrationId,
        notes: {
          reference: notes.reference,
          admin: notes.admin,
          driver: notes.driverNotes,
        },
        key_contact: selectedCompany.keyContact,
        invoice: {
          enabled: invoice.enabled,
          generate_on_level: invoice.generateOnLevel ? invoice.generateOnLevel : undefined,
          generate_on_frequency: invoice.generateOnFrequency ? invoice.generateOnFrequency : undefined,
          generate_on_date: invoice.generateOnDate ? invoice.generateOnDate : undefined,
          payment_terms: invoice.paymentTerms ? invoice.paymentTerms : undefined,
        },
        billing_contacts: selectedCompany.billingContacts,
        pricing: {
          adjustments: pricing.adjustments,
          commission: {
            override_enabled: pricing.commission.overrideEnabled,
            percentage: pricing.commission.percentage ? pricing.commission.percentage : undefined,
          },
          profile_uuid: "",
          allow_driver_extras: pricing.allowDriverExtras,
          include_client_extras_in_base_cost: false,
        },
      };

      updateCompany(companyData)
        .unwrap()
        .then(() => {
          addToast("success", "Successfully Removed Company from Pricing Profile");
          handleClose();
        })
        .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
    } else handleClose();
  };

  return (
    <Modal
      className="w-[85vw] md:w-[400px] lg:w-[400px]"
      open={open}
      onClose={handleClose}
    >
      <div className="relative flex flex-col">
        {isLoading && <Loading />}
        <Typography variant="title">Remove Company from Pricing Profile</Typography>
        <Typography variant="paragraph" className="mt-2">Are you sure you want to remove this company from this pricing profile?</Typography>
        <div className="flex flex-row w-full gap-3 mt-8">
          <Button variant="secondary" size="lg" onClick={handleClose} className="flex-1">
            Cancel
          </Button>
          <Button variant="primary" size="lg" onClick={handleConfirmDelete} className="flex-1 bg-danger border-none hover:bg-danger-dark">
            Remove
          </Button>
        </div>
      </div>
    </Modal>
  );
};
